const SECTION_SELECTOR = '[data-store-locator-section]';
const LIBRARY_URL = 'https://ajax.googleapis.com/ajax/libs/@googlemaps/extended-component-library/0.6.15/index.min.js';
const DIRECTIONS_STYLE_ID = 'store-locator-directions-style';

let libraryPromise;
const observedLocators = new WeakSet();

const loadLibrary = () => {
  if (!libraryPromise) {
    libraryPromise = import(LIBRARY_URL);
  }

  return libraryPromise;
};

const parseConfiguration = (sectionElement) => {
  const payloadNode = sectionElement.querySelector('[data-store-locator-settings]');

  if (!payloadNode) {
    throw new Error('Missing configuration payload');
  }

  const serializedSetting = JSON.parse(payloadNode.textContent);
  return JSON.parse(serializedSetting);
};

const actionUrl = (action) => action.defaultUrl || action.defaultUri || action.url || action.href || '';

const normalizeAction = (action) => {
  const label = (action.label || action.text || '').trim();
  const defaultUrl = actionUrl(action).trim();

  if (!label || !defaultUrl) {
    return null;
  }

  return { label, defaultUrl };
};

const normalizeLocations = (configuration) => {
  const locations = (configuration.locations || []).map((location) => {
    const actions = (location.actions || []).map(normalizeAction).filter(Boolean);

    return { ...location, actions };
  });

  return { ...configuration, locations };
};

const injectDirectionsStyles = (root) => {
  if (root.getElementById(DIRECTIONS_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = DIRECTIONS_STYLE_ID;
  style.textContent = `
    #location-results-list .result-item,
    #location-results-list .name,
    #location-results-list .address,
    #location-results-list .address p,
    #location-results-list .distance {
      text-align: start;
    }

    #location-results-list .result-item {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-areas:
        "name"
        "distance"
        "address"
        "actions";
      justify-items: start;
      align-items: start;
      gap: 0.5em;
      padding: 0.8em 1.4em;
    }

    #location-results-list .name {
      grid-area: name;
      display: block;
      margin: 0;
      width: 100%;
      min-width: 0;
      overflow-wrap: anywhere;
      text-align: start !important;
    }

    #location-results-list .address {
      grid-area: address;
      margin-bottom: 0;
      width: 100%;
      min-width: 0;
    }

    #location-results-list .distance {
      grid-area: distance;
      position: static;
      top: auto;
      right: auto;
      width: auto;
    }

    #location-results-list .actions {
      grid-area: actions;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: center;
      gap: 0.5em;
      width: auto;
      max-width: 100%;
    }

    #location-results-list .actions > *,
    #location-results-list .actions button,
    #location-results-list .actions a,
    #location-results-list gmpx-icon-button,
    #location-results-list gmpx-place-directions-button {
      width: auto !important;
      max-width: 100%;
      flex: 0 0 auto;
      align-self: start;
    }

    #location-results-list gmpx-place-directions-button {
      position: static !important;
      inset: auto;
      display: inline-flex;
    }

    @media screen and (min-width: 700px) {
      #location-results-list .result-item {
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-areas:
          "name distance"
          "address address"
          "actions actions";
        column-gap: 0.75em;
      }
    }
  `;
  root.appendChild(style);
};

const ensureActions = (item, root) => {
  const existing = item.querySelector('.actions');

  if (existing) {
    return existing;
  }

  const actions = root.ownerDocument.createElement('div');
  actions.className = 'actions';
  item.appendChild(actions);
  return actions;
};

const enhanceDirectionsButtons = (locator, label) => {
  const root = locator.shadowRoot;

  if (!root || !label) {
    return;
  }

  injectDirectionsStyles(root);

  root.querySelectorAll('gmpx-place-directions-button').forEach((button) => {
    button.condensed = false;
    button.removeAttribute('condensed');

    if (button.textContent.trim() !== label) {
      button.textContent = label;
    }

    const item = button.closest('.result-item');

    if (!item) {
      return;
    }

    const actions = ensureActions(item, root);

    if (button.parentElement !== actions) {
      actions.appendChild(button);
    }
  });
};

const isInternalLink = (anchor) => {
  const href = anchor.getAttribute('href') || '';

  if (!href || href.startsWith('#')) {
    return false;
  }

  try {
    return new URL(href, window.location.href).origin === window.location.origin;
  } catch (_error) {
    return false;
  }
};

// Google's store locator renders its action links with target="_blank". Store
// pages belong in the same tab; the Directions button still hands off to Google
// Maps in a new one, which is where a new tab actually helps.
const keepInternalLinksInTab = (locator) => {
  const root = locator.shadowRoot;

  if (!root) {
    return;
  }

  root.querySelectorAll('a[target]').forEach((anchor) => {
    if (!isInternalLink(anchor)) {
      return;
    }

    anchor.removeAttribute('target');
    anchor.removeAttribute('rel');
  });
};

const observeDirectionsButtons = (locator, label) => {
  const root = locator.shadowRoot;

  if (!root) {
    return;
  }

  enhanceDirectionsButtons(locator, label);
  keepInternalLinksInTab(locator);

  if (observedLocators.has(locator)) {
    return;
  }

  observedLocators.add(locator);

  const observer = new MutationObserver(() => {
    enhanceDirectionsButtons(locator, label);
    keepInternalLinksInTab(locator);
  });

  observer.observe(root, { childList: true, subtree: true });
};

const configureLocator = async (sectionElement) => {
  const widget = sectionElement.querySelector('[data-store-locator-widget]');
  const locator = sectionElement.querySelector('[data-store-locator-element]');
  const configError = sectionElement.querySelector('[data-store-locator-config-error]');
  const apiLoader = sectionElement.querySelector('gmpx-api-loader');

  if (!widget || !locator || !configError || !apiLoader) {
    return;
  }

  const mapsApiKey = apiLoader.getAttribute('key') || '';
  const mapId = sectionElement.dataset.mapId || locator.getAttribute('map-id') || '';

  if (!mapsApiKey) {
    widget.classList.add('hidden');
    configError.classList.add('hidden');
    return;
  }

  let configuration;
  try {
    configuration = normalizeLocations(parseConfiguration(sectionElement));
  } catch (_error) {
    widget.classList.add('hidden');
    configError.classList.remove('hidden');
    return;
  }

  configError.classList.add('hidden');
  widget.classList.remove('hidden');

  const mapOptions = configuration.mapOptions || {};
  const defaultZoom = Number(sectionElement.dataset.defaultZoom);

  configuration.mapOptions = {
    ...mapOptions,
    mapId,
    zoomControl: sectionElement.dataset.zoomControl === 'true',
    fullscreenControl: sectionElement.dataset.fullscreenControl === 'true',
    streetViewControl: sectionElement.dataset.streetViewControl === 'true',
    mapTypeControl: sectionElement.dataset.mapTypeControl === 'true',
    zoom: Number.isFinite(defaultZoom) && defaultZoom > 0 ? defaultZoom : mapOptions.zoom
  };
  configuration.mapsApiKey = mapsApiKey;

  apiLoader.setAttribute('key', mapsApiKey);
  locator.setAttribute('map-id', mapId);

  await loadLibrary();
  await customElements.whenDefined('gmpx-store-locator');
  locator.configureFromQuickBuilder(configuration);

  if (locator.updateComplete) {
    await locator.updateComplete;
  }

  observeDirectionsButtons(locator, sectionElement.dataset.directionsLabel || '');
};

const initAllLocators = () => {
  document.querySelectorAll(SECTION_SELECTOR).forEach((sectionElement) => {
    configureLocator(sectionElement);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllLocators);
} else {
  initAllLocators();
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionElement = event.target.querySelector(SECTION_SELECTOR);

  if (!sectionElement) {
    return;
  }

  configureLocator(sectionElement);
});
